import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMasterData } from '../../hooks/useMasterData';
import { useAuth } from '../../hooks/useAuth';
import { useAuthorization } from '../../hooks/useAuthorization';
import { useDateContext } from '../../hooks/useDate';
import { useGlobalKeyboard, KEYBOARD_PRIORITIES } from '../../hooks/useGlobalKeyboard';
import { useCaseHelpers } from '../../hooks/useCaseHelpers';
import { LandTransporter, Case, DispatchPlan, Role, DriverScheduleVisibility } from '../../types';
import { generateDispatchPlan } from '../../services/geminiService';
import {
  generateDynamicTimeSlotsFromSchedules,
  DEFAULT_TIME_SLOTS,
} from '../../utils/timeSlotUtils';
import { DragStartEvent, DragEndEvent, DragOverEvent } from '@dnd-kit/core';
import logger from '../../utils/logger';
import { useDragDropSchedule } from '../../hooks/useDragDropSchedule';
import { getCompanyScheduleVisibility } from '../../services/supabaseScheduleService';
import { checkDriverScheduleEditPermission } from '../../services/supabase/supabaseCompanyService';
import { getScheduleForDate } from '../../services/driverScheduleService';
import {
  ScheduleDragDropContext,
  DroppableZone,
  DraggableCase,
  SharedTimeAxisHeader,
  TransporterRowOptimized,
} from '../../components/schedule';
import AiReasonDisplay from '../../components/schedule/AiReasonDisplay';
import { TimeDisplay } from '../../components/schedule/TimeDisplay';
import LandTransporterDetailModal from '../../components/master/LandTransporterDetailModal';
import { ScheduleDirectRegistrationModal } from '../../components/schedule/ScheduleDirectRegistrationModal';
import type { ScheduleRegistrationFormData } from '../../types';
import {
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  XMarkIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { PageTitle } from '../../components/ui';
import { getCaseStatusText, getCaseStatusClass } from '../../utils/statusUtils';

// daysOfWeekは共通コンポーネントに移動済み

const SchedulePage: React.FC = () => {
  const navigate = useNavigate();
  const {
    safe: {
      getVehicleNameForSchedule,
      getBillingPartnerName,
      getPickupPartnerName,
      getDeliveryPartnerName,
    },
    formatDateForDisplay,
  } = useCaseHelpers();

  // 案件詳細ページへのナビゲーションヘルパー
  const navigateToCaseDetail = (caseId: string) => {
    navigate(`/cases/${caseId}`);
  };

  const {
    landTransporters,
    cases,
    vehicleTypes,
    // getVehicleById,
    updateCase,
    refreshData,
    refreshLandTransporters,
  } = useMasterData();
  const { user } = useAuth();
  const { checkPermission } = useAuthorization();
  const { selectedDate, setSelectedDate, goToPreviousDay, goToNextDay } = useDateContext();
  const [searchParams, setSearchParams] = useSearchParams();

  // FL-180: 業務制約の参照権限チェック（SUPER_ADMIN, ADMIN, DISPATCHERのみ）
  const canViewBusinessConstraints = checkPermission([
    Role.SUPER_ADMIN,
    Role.ADMIN,
    Role.DISPATCHER,
  ]);

  // 統一キーボード管理フック
  const { registerHandler } = useGlobalKeyboard();
  const [selectedTransporter, setSelectedTransporter] = useState<LandTransporter | null>(null);

  // AI配車関連のstate
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [aiDispatchPlan, setAiDispatchPlan] = useState<DispatchPlan[]>([]);
  const [aiError, setAiError] = useState<string>('');
  const [showAiResults, setShowAiResults] = useState(false);

  // ドラッグ&ドロップ関連のstate
  const [activeCase, setActiveCase] = useState<Case | null>(null);
  const [dragOverTransporter, setDragOverTransporter] = useState<LandTransporter | null>(null);
  const [canDrop, setCanDrop] = useState<boolean>(true);
  const [dropReason, setDropReason] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState<boolean>(false); // 案件更新中フラグ（Race Condition防止）

  // 時間軸表示モードのstate
  const [timeSlotMode, setTimeSlotMode] = useState<'dynamic' | 'fixed'>('dynamic');

  // FL-325: スケジュール表示倍率の状態管理
  const [scheduleScale, setScheduleScale] = useState<100 | 90 | 80>(100);

  // FL-563: driver_schedules キャッシュ（実際の登録スケジュール）
  // DriverScheduleは src/types.ts から暗黙的にインポート（9行目で DriverScheduleVisibility を同じ場所から取得）
  const [scheduleCache, setScheduleCache] = useState<Record<string, any | null>>({});

  // FL-163: スケジュール直接登録モーダルの状態管理
  const [isDirectRegistrationModalOpen, setIsDirectRegistrationModalOpen] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{
    transporterId: string;
    timeSlot: string;
    date: string;
  } | null>(null);

  // AI配車モードの判定
  const isAiMode = searchParams.get('ai') === 'true';

  // 陸送員かどうかの判定
  const isDriver = user?.role === Role.DRIVER;

  // FL-114: スケジュール可視性設定の状態管理
  const [scheduleVisibility, setScheduleVisibility] = useState<
    DriverScheduleVisibility | undefined
  >();

  // FL-115: スケジュール編集権限設定の状態管理
  const [canEditSchedule, setCanEditSchedule] = useState<boolean>(false);

  // FL-163: 空の時間スロットクリック時の処理
  const handleTimeSlotClick = useCallback(
    (transporterId: string, timeSlot: string, date: string) => {
      logger.info('🔵 時間スロットクリック:', { transporterId, timeSlot, date });
      // 新規案件登録画面に遷移（クエリパラメータで選択情報を渡す）
      navigate(
        `/cases/new?transporterId=${transporterId}&pickupDate=${date}&pickupTimeStart=${timeSlot}`
      );
    },
    [navigate]
  );

  // FL-163: スケジュール直接登録の保存処理
  const handleDirectRegistrationSave = useCallback(
    async (data: ScheduleRegistrationFormData) => {
      if (!selectedTimeSlot || !user?.companyId) return;

      try {
        logger.info('💾 スケジュール直接登録保存開始:', data);

        const { SupabaseCaseService } = await import('../../services/supabase/supabaseCaseService');
        const caseService = new SupabaseCaseService();

        // Case型の必須フィールドにデフォルト値を設定
        await caseService.createCase({
          companyId: user.companyId,
          status: 'assigned' as const,
          assignedLandTransporterId: selectedTimeSlot.transporterId,

          // 取引先情報（任意、後から入力可能）
          billingPartnerId: data.billingPartnerId || '',
          pickupPartnerId: data.pickupPartnerId || '',
          deliveryPartnerId: data.deliveryPartnerId || '',

          // レガシーClientID（後方互換性のため空文字列）
          billingClientId: '',
          pickupClientId: '',
          deliveryClientId: '',

          // 引き取り情報
          pickupAddress: '', // 後から入力
          pickupPhone: '', // 後から入力
          pickupDate: selectedTimeSlot.date,
          pickupTimeStart: selectedTimeSlot.timeSlot,
          pickupTimeEnd: selectedTimeSlot.timeSlot, // デフォルトは同じ時刻
          pickupReport: null,

          // 搬入情報
          deliveryAddress: '', // 後から入力
          deliveryPhone: '', // 後から入力
          deliveryDate: selectedTimeSlot.date, // デフォルトは同日
          deliveryTime: selectedTimeSlot.timeSlot, // デフォルトは同じ時刻
          deliveryReport: null,

          // 車両情報
          vehicleModel: '', // 後から入力
          vehicleModelId: data.vehicleModelId,
          vehicleType: '', // 後から入力
          vehiclePlateNumber: data.vehiclePlateNumber || '',
          chassisNumber: data.chassisNumber,

          // その他
          price: 0,
          remarks: data.remarks || '',
        });

        logger.info('✅ 案件作成完了');
        setIsDirectRegistrationModalOpen(false);
        setSelectedTimeSlot(null);
        refreshData();
      } catch (error) {
        logger.error('❌ 案件作成失敗:', error);
        alert('案件の作成に失敗しました。');
      }
    },
    [selectedTimeSlot, user?.companyId, refreshData]
  );

  // FL-163: スケジュール直接登録のキャンセル
  const handleDirectRegistrationCancel = useCallback(() => {
    setIsDirectRegistrationModalOpen(false);
    setSelectedTimeSlot(null);
  }, []);

  // 案件を未割り当てに戻すハンドラー
  const handleUnassignCase = async (caseId: string) => {
    // FL-115: スケジュール編集権限チェック
    if (isDriver && !canEditSchedule) {
      logger.warn('❌ スケジュール編集権限なし: 未割り当て操作を拒否');
      return;
    }

    try {
      logger.info('🔄 案件未割り当て処理開始:', caseId);

      const updateData = {
        assignedLandTransporterId: null, // undefinedではなくnullを明示的に設定
        status: 'confirmed' as const, // FL-142: 未割当時は本受・未配車ステータスに設定
      };

      logger.info('📤 送信データ:', updateData);

      await updateCase(caseId, updateData);

      logger.info('✅ 案件未割り当て処理完了、データ再取得開始');

      // データを再取得して最新状態に更新
      refreshData();
    } catch (error) {
      logger.error('❌ 案件の未割り当て処理に失敗しました:', error);
      alert('未割り当て処理に失敗しました。もう一度お試しください。');
    }
  };

  // ページがマウントされた時にデータを最新化
  useEffect(() => {
    refreshData();
  }, [refreshData]);

  // ページがフォーカスされた時にもデータを更新
  useEffect(() => {
    const handleFocus = () => {
      refreshData();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refreshData]);

  // 陸送員データの変更を監視
  useEffect(() => {
    // landTransportersが変更された時の処理
  }, [landTransporters]);

  // FL-114 & FL-115: 会社のスケジュール設定を取得
  useEffect(() => {
    // noImplicitReturns 対応のため、常にクリーンアップを返す
    let isMounted = true;

    if (user?.companyId && user?.id) {
      const loadScheduleSettings = async () => {
        try {
          logger.info('🔄 スケジュール設定取得開始', {
            companyId: user.companyId,
            userId: user.id,
          });

          // FL-114: スケジュール可視性設定を取得
          const visibility = await getCompanyScheduleVisibility(user.companyId!);
          if (!isMounted) return;
          setScheduleVisibility(visibility);

          // FL-115: スケジュール編集権限をチェック
          const canEdit = await checkDriverScheduleEditPermission(
            user.companyId!,
            user.id,
            user.landTransporterId
          );
          if (!isMounted) return;
          setCanEditSchedule(canEdit);

          logger.info('✅ スケジュール設定取得完了', { visibility, canEdit });
        } catch (error) {
          logger.error('❌ スケジュール設定取得エラー:', error);
          // エラー時はデフォルト値を使用
          if (!isMounted) return;
          setScheduleVisibility(DriverScheduleVisibility.ADMIN_ONLY);
          setCanEditSchedule(false);
        }
      };

      loadScheduleSettings();
    }

    return () => {
      isMounted = false;
    };
  }, [user?.companyId, user?.id, user?.landTransporterId]);

  // FL-563: selectedDate変更時に全陸送員のスケジュールを事前取得
  useEffect(() => {
    if (!landTransporters || landTransporters.length === 0) {
      return;
    }

    let isMounted = true;

    const preloadSchedules = async () => {
      try {
        logger.info('🔄 スケジュール事前取得開始:', {
          date: selectedDate,
          transporterCount: landTransporters.length,
        });

        const cache: Record<string, any | null> = {};

        // 全陸送員のスケジュールを並列取得
        await Promise.all(
          landTransporters.map(async transporter => {
            try {
              const schedule = await getScheduleForDate(transporter.id, selectedDate);

              // デバッグログ: 各陸送員の取得結果
              logger.info(`🔍 [preloadSchedules] ${transporter.name} - 取得結果:`, {
                transporterId: transporter.id,
                selectedDate,
                scheduleFound: !!schedule,
                schedule: schedule || null,
                timeStart: schedule?.timeStart,
                timeEnd: schedule?.timeEnd,
              });

              if (isMounted) {
                cache[transporter.id] = schedule;
              }
            } catch (error) {
              logger.error(`スケジュール取得エラー (${transporter.name}):`, error);
              if (isMounted) {
                cache[transporter.id] = null;
              }
            }
          })
        );

        if (isMounted) {
          setScheduleCache(cache);

          // デバッグログ: キャッシュ構築完了詳細
          logger.info('✅ [preloadSchedules] キャッシュ構築完了:', {
            date: selectedDate,
            cacheKeys: Object.keys(cache),
            cacheValues: Object.entries(cache).map(([id, schedule]) => ({
              transporterId: id,
              hasSchedule: schedule !== null && schedule !== undefined,
              timeStart: schedule?.timeStart,
              timeEnd: schedule?.timeEnd,
            })),
            registeredCount: Object.values(cache).filter(s => s !== null).length,
          });
        }
      } catch (error) {
        logger.error('❌ スケジュール事前取得エラー:', error);
      }
    };

    preloadSchedules();

    return () => {
      isMounted = false;
    };
  }, [selectedDate, landTransporters]);

  // AI配車関連のハンドラー関数
  const handleCloseAiResults = useCallback(() => {
    setShowAiResults(false);
    setAiDispatchPlan([]);
    setAiError('');
  }, []);

  // ESCキーでモーダルを閉じる（統一キーボード管理）
  useEffect(() => {
    const unregister = registerHandler({
      id: 'schedule-page-escape',
      priority: KEYBOARD_PRIORITIES.PAGE_SHORTCUTS,
      keys: ['Escape'],
      shouldIgnoreInputFocus: true, // 入力フィールドフォーカス時も動作
      handler: (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          if (selectedTransporter) {
            setSelectedTransporter(null);
            return true; // 処理済み
          } else if (showAiResults) {
            handleCloseAiResults();
            return true; // 処理済み
          }
        }
        return false; // 未処理
      },
    });

    return unregister;
  }, [selectedTransporter, showAiResults, registerHandler, handleCloseAiResults]);

  // DateContextのメソッドを直接使用
  const handlePreviousDay = goToPreviousDay;
  const handleNextDay = goToNextDay;

  // FL-563: working_days完全廃止に伴い、selectedDayOfWeekはavailableTransportersフィルタリングや
  // 時間軸生成では使用していない。ただし useDragDropSchedule や scheduleValidationService の
  // 互換性のため、一時的に計算を保持（将来の大規模リファクタリングで削除予定）
  const selectedDayOfWeek = useMemo(() => {
    // ISO文字列として解釈してタイムゾーンの問題を避ける
    const date = new Date(selectedDate + 'T00:00:00');
    const dayIndex = date.getDay();
    const dayMapping = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ];
    const result = dayMapping[dayIndex] as keyof LandTransporter['workingDays'];
    return result;
  }, [selectedDate]);

  const availableTransporters = useMemo(() => {
    // 陸送員データが空の場合は早期リターン
    if (!landTransporters || landTransporters.length === 0) {
      logger.info('🔍 Schedule Debug: 陸送員データが空です', { landTransporters });
      return [];
    }

    logger.info('🔍 Schedule Debug: フィルタリング前', {
      landTransportersCount: landTransporters.length,
      allTransporterNames: landTransporters.map(t => t.name),
      sampleTransporter: landTransporters[0],
    });

    // FL-563 修正: driver_schedules（実登録スケジュール）を優先使用
    // scheduleCache に値がない場合でも、working_days を使用して陸送員を表示
    let filtered = landTransporters.filter(transporter => {
      const registeredSchedule = scheduleCache[transporter.id];
      const hasWorkingDays = transporter.workingDays && transporter.workingDays[selectedDayOfWeek];

      // 陸送員は以下いずれかのスケジュール定義があれば表示対象
      // 1. scheduleCache が有効: driver_schedules（実登録スケジュール）あり
      // 2. working_days が有効: 曜日別デフォルト勤務時間あり
      const hasSchedule =
        (registeredSchedule !== undefined && registeredSchedule !== null) || hasWorkingDays;

      // デバッグログ: フィルタリング詳細
      logger.info(`🔍 [availableTransporters] ${transporter.name}:`, {
        transporterId: transporter.id,
        registeredSchedule,
        hasWorkingDays,
        hasSchedule,
        timeStart: registeredSchedule?.timeStart,
        timeEnd: registeredSchedule?.timeEnd,
      });

      return hasSchedule;
    });

    logger.info('🔍 Schedule Debug: フィルタリング後', {
      filteredCount: filtered.length,
      filteredNames: filtered.map(t => t.name),
    });

    // FL-114: 陸送員の場合のスケジュール表示制御
    if (isDriver && user?.landTransporterId && scheduleVisibility !== undefined) {
      logger.info('🔍 Schedule Visibility Control:', {
        scheduleVisibility,
        isDriver,
        landTransporterId: user.landTransporterId,
      });

      // 設定が「管理者のみ」の場合は自分のスケジュールのみ表示
      if (scheduleVisibility === DriverScheduleVisibility.ADMIN_ONLY) {
        logger.info('📋 陸送員表示制限: 自分のみ表示');
        filtered = filtered.filter(transporter => transporter.id === user.landTransporterId);
      } else {
        logger.info('👥 陸送員表示制限: 全陸送員表示');
        // 設定が「全陸送員」の場合は全員表示（フィルタリングしない）
      }
    }

    // AI配車モードの場合、AI提案に含まれる陸送員のみフィルタ
    if (isAiMode && aiDispatchPlan.length > 0) {
      const aiTransporterIds = aiDispatchPlan.map(plan => plan.landTransporterId);
      filtered = filtered.filter(transporter => aiTransporterIds.includes(transporter.id));
    }

    return filtered.sort((a, b) => a.name.localeCompare(b.name, 'ja'));
  }, [
    landTransporters,
    selectedDayOfWeek,
    isDriver,
    user?.landTransporterId,
    isAiMode,
    aiDispatchPlan,
    scheduleVisibility,
    scheduleCache,
  ]);

  // 動的時間軸の生成
  // FL-563: driver_schedules（実登録スケジュール）から時間軸を生成
  const timeSlots = useMemo(() => {
    logger.info('📅 Schedule TimeSlots: 時間軸生成', {
      timeSlotMode,
      availableTransportersCount: availableTransporters.length,
      availableTransportersNames: availableTransporters.map(t => t.name),
    });

    if (timeSlotMode === 'fixed') {
      logger.info('📅 Schedule TimeSlots: 固定時間軸モード使用');
      return [...DEFAULT_TIME_SLOTS];
    }

    // availableTransporters の陸送員について、scheduleCache から実際のスケジュール情報を取得
    const schedules = availableTransporters
      .map(transporter => scheduleCache[transporter.id])
      .filter(
        (schedule): schedule is NonNullable<typeof schedule> =>
          schedule !== null && schedule !== undefined
      )
      .map(schedule => ({
        timeStart: schedule.timeStart,
        timeEnd: schedule.timeEnd,
      }));

    const dynamicSlots = generateDynamicTimeSlotsFromSchedules(schedules);
    logger.info('📅 Schedule TimeSlots: 動的時間軸結果', {
      scheduleCount: schedules.length,
      timeSlots: dynamicSlots,
    });
    return dynamicSlots;
  }, [availableTransporters, scheduleCache, timeSlotMode]);

  const todaysCases = useMemo(() => {
    // 選択日の引き取り案件と、前日から継続中の案件を含める
    let filteredCases = cases.filter(case_ => {
      // 選択日が引き取り日の案件
      if (case_.pickupDate === selectedDate) {
        return true;
      }

      // 前日以前に引き取り開始し、選択日にまだ搬入完了していない案件
      if (case_.pickupDate < selectedDate && case_.deliveryDate >= selectedDate) {
        return true;
      }

      return false;
    });

    // FL-114: 陸送員の場合の案件表示制御
    if (isDriver && user?.landTransporterId && scheduleVisibility !== undefined) {
      // 設定が「管理者のみ」の場合は自分に割り当てられた案件のみ表示
      if (scheduleVisibility === DriverScheduleVisibility.ADMIN_ONLY) {
        filteredCases = filteredCases.filter(
          case_ => case_.assignedLandTransporterId === user.landTransporterId
          // 自分に割り当てられた案件のみ表示（未割り当ては自動的に除外される）
        );
      }
      // 設定が「全陸送員」の場合は全案件表示（フィルタリングしない）
    }

    return filteredCases;
  }, [cases, selectedDate, isDriver, user?.landTransporterId, scheduleVisibility]);

  const unassignedCases = useMemo(() => {
    return todaysCases.filter(case_ => !case_.assignedLandTransporterId);
  }, [todaysCases]);

  // useDragDropSchedule フック：dragDropService + ScheduleValidationService 統合
  // 状態管理・バリデーション・ハンドラーを統一化
  // 注: ここで定義しているのは、依存する useMemo (todaysCases, availableTransporters, selectedDayOfWeek) が先に定義されているため
  const dragDropResult = useDragDropSchedule({
    cases: todaysCases,
    availableTransporters,
    selectedDate,
    selectedDayOfWeek,
    onCaseUpdate: async (caseId: string, updates: Partial<Case>) => {
      await updateCase(caseId, updates);
    },
    onOperationComplete: async result => {
      logger.info('✅ ドラッグ&ドロップ操作完了:', result);
      await refreshData();
    },
    onValidationError: validationResult => {
      logger.warn('❌ バリデーションエラー:', validationResult.reason);
    },
  });

  // ドラッグ&ドロップハンドラー：useDragDropSchedule フック取得後の定義
  const handleDndDragStart = useCallback(
    (event: DragStartEvent) => {
      // 権限チェック：スケジュール編集権限がない場合はドラッグ開始を阻止
      if (isDriver && !canEditSchedule) {
        logger.warn('❌ スケジュール編集権限なし: ドラッグ開始を拒否');
        return;
      }
      // useDragDropSchedule の handleDragStart を使用
      dragDropResult.handleDragStart(event);
    },
    [dragDropResult, isDriver, canEditSchedule]
  );

  // 割り当て可能性の統合チェック関数
  // ScheduleValidationService を使用してバリデーション統一化
  const canAssignCase = useCallback(
    async (
      newCase: Case,
      transporterId: string
    ): Promise<{ canAssign: boolean; reason?: string }> => {
      // dragDropResult.validateCaseAssignmentを使用（async Hook）
      const result = await dragDropResult.validateCaseAssignment(newCase, transporterId);
      return {
        canAssign: result.canAssign,
        reason: result.reason || (result.canAssign ? '' : '割り当てできません'),
      };
    },
    [dragDropResult]
  );

  const handleDndDragOver = useCallback(
    (event: DragOverEvent) => {
      // useDragDropSchedule の handleDragOver で統合バリデーション実行
      dragDropResult.handleDragOver(event);
    },
    [dragDropResult]
  );

  /**
   * ドロップ位置から時間を計算する関数
   * @param dropZoneId - ドロップゾーンID（"transporter-{transporterId}-time-{timeSlot}" または "transporter-{transporterId}" 形式）
   * @returns 抽出された時間文字列（HH:mm形式）、または undefined
   */
  const calculateTimeFromDropPosition = useCallback((dropZoneId: string): string | undefined => {
    const TIME_SLOT_PATTERN = /time-(\d{2}:\d{2})/;
    const timeMatch = dropZoneId.match(TIME_SLOT_PATTERN);
    if (timeMatch) {
      const extractedTime = timeMatch[1];
      // 時間の妥当性をチェック
      const [hours, minutes] = extractedTime.split(':').map(Number);
      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        return extractedTime;
      }
    }
    return undefined;
  }, []);

  const handleDndDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      // FL-167: Race Condition防止 - 既に更新処理中の場合は拒否
      if (isUpdating) {
        logger.warn('⚠️ 案件更新中のため、ドロップを無視');
        return;
      }

      if (!over) {
        logger.info('🔍 DragEnd: ドロップ先なし');
        // ドロップ先がない場合も状態をリセット
        setActiveCase(null);
        setDragOverTransporter(null);
        setCanDrop(true);
        setDropReason('');
        return;
      }

      // FL-115: スケジュール編集権限チェック
      if (isDriver && !canEditSchedule) {
        logger.warn('❌ スケジュール編集権限なし: 配車操作を拒否');
        // 権限がない場合も状態をリセット
        setActiveCase(null);
        setDragOverTransporter(null);
        setCanDrop(true);
        setDropReason('');
        return;
      }

      const caseData = active.data.current?.case as Case;
      const dropData = over.data.current;

      logger.info('🔍 DragEnd: 詳細ドロップ情報', {
        caseId: caseData?.id,
        dropZoneId: over.id,
        dropType: dropData?.type,
        hasLandTransporter: !!dropData?.landTransporter,
        isUnassignedZone: over.id === 'unassigned-zone',
        isUnassignedType: dropData?.type === 'unassigned',
        overData: over.data?.current,
        activeData: active.data?.current,
      });

      if (!caseData) {
        // 案件データがない場合も状態をリセット
        setActiveCase(null);
        setDragOverTransporter(null);
        setCanDrop(true);
        setDropReason('');
        return;
      }

      try {
        setIsUpdating(true); // ロック獲得

        let updatedCaseData: Partial<Case> = {};

        // 未割り当てゾーンへのドロップを優先処理（通常エリア + フローティングエリア）
        if (
          over.id === 'unassigned-zone' ||
          over.id === 'floating-unassigned-zone' ||
          dropData?.type === 'unassigned'
        ) {
          logger.info('✅ 未割り当てエリアにドロップ', { zoneId: over.id });
          updatedCaseData = {
            assignedLandTransporterId: undefined,
            status: 'confirmed' as const, // FL-142: 未割当時は本受・未配車
          };
        } else if (dropData?.type === 'transporter' && dropData.landTransporter) {
          logger.info('🎯 陸送員エリアにドロップ', dropData.landTransporter.name);
          // 統合チェック（勤務時間外 + 時間重複）
          const assignmentResult = await canAssignCase(caseData, dropData.landTransporter.id);

          if (!assignmentResult.canAssign) {
            logger.info('❌ 割り当て不可:', assignmentResult.reason);
            return; // サイレントに処理を停止（視覚的フィードバックで既に通知済み）
          }

          // ドロップ位置から時間を計算
          const scheduledTime = calculateTimeFromDropPosition(over.id.toString());

          // 陸送員の列にドロップ
          updatedCaseData = {
            assignedLandTransporterId: dropData.landTransporter.id,
            status: 'assigned' as const,
            ...(scheduledTime && { scheduledTime }), // 時間が計算できた場合のみ追加
          };

          logger.info('⏰ スケジュール時間設定:', { scheduledTime, dropZoneId: over.id });
        } else {
          logger.info('⚠️ 不明なドロップ先:', over.id);
        }

        if (Object.keys(updatedCaseData).length > 0) {
          logger.info('🔄 案件更新実行:', updatedCaseData);
          await updateCase(caseData.id, updatedCaseData);

          // 更新完了後、明示的にデータ再取得（ローカルstateとDBの同期）
          logger.info('🔄 データ再取得開始');
          await refreshData();

          logger.info('✅ 案件更新完了');
        }
      } catch (error) {
        logger.error('案件の更新に失敗しました:', error);
        alert(`案件の更新に失敗しました: ${error}`);
      } finally {
        // FL-177: チラつき解消 - refreshData()完了後に状態リセット
        // これにより、UIが古い状態でレンダリングされるのを防ぐ
        setActiveCase(null);
        setDragOverTransporter(null);
        setCanDrop(true);
        setDropReason('');
        setIsUpdating(false); // ロック解放
      }
    },
    [
      updateCase,
      canAssignCase,
      isUpdating,
      refreshData,
      isDriver,
      canEditSchedule,
      calculateTimeFromDropPosition,
    ]
  );

  // const getCaseForTimeSlot = (transporterId: string, timeSlot: string) => {
  //     return todaysCases.find(case_ => {
  //         if (case_.assignedLandTransporterId !== transporterId) return false;
  //
  //         // 跨日案件に対応した時間判定
  //         if (case_.pickupDate === selectedDate) {
  //             // 選択日が引き取り日の場合
  //             if (case_.deliveryDate === selectedDate) {
  //                 // 同日搬入: 引き取り開始から搬入時間まで
  //                 const startTime = case_.pickupTimeStart;
  //                 const endTime = case_.deliveryTime;
  //                 return startTime <= timeSlot && timeSlot < endTime;
  //             } else {
  //                 // 翌日以降搬入: 引き取り開始から終業時間まで
  //                 const startTime = case_.pickupTimeStart;
  //                 return startTime <= timeSlot;
  //             }
  //         } else if (case_.pickupDate < selectedDate && case_.deliveryDate > selectedDate) {
  //             // 選択日が移動・待機日の場合: 全日占有
  //             return true;
  //         } else if (case_.deliveryDate === selectedDate) {
  //             // 選択日が搬入日の場合: 始業時間から搬入時間まで
  //             const endTime = case_.deliveryTime;
  //             return timeSlot < endTime;
  //         }
  //
  //         return false;
  //     });
  // };

  // 案件の時間帯タイプを判定する関数（跨日対応）- 現在未使用
  // const getCaseTimeSlotType = (case_: Case, timeSlot: string): 'pickup' | 'transit' | 'delivery' => {
  //     if (case_.pickupDate === selectedDate) {
  //         // 選択日が引き取り日の場合
  //         if (case_.deliveryDate === selectedDate) {
  //             // 同日搬入
  //             if (timeSlot >= case_.pickupTimeStart && timeSlot < case_.pickupTimeEnd) {
  //                 return 'pickup'; // 引き取り時間帯
  //             } else if (timeSlot >= case_.pickupTimeEnd && timeSlot < case_.deliveryTime) {
  //                 return 'transit'; // 移動・待機時間帯
  //             } else if (timeSlot >= case_.deliveryTime) {
  //                 return 'delivery'; // 搬入時間帯（終了後）
  //             }
  //         } else {
  //             // 翌日以降搬入
  //             if (timeSlot >= case_.pickupTimeStart && timeSlot < case_.pickupTimeEnd) {
  //                 return 'pickup'; // 引き取り時間帯
  //             } else {
  //                 return 'transit'; // 移動・待機時間帯
  //             }
  //         }
  //     } else if (case_.pickupDate < selectedDate && case_.deliveryDate > selectedDate) {
  //         // 選択日が移動・待機日
  //         return 'transit';
  //     } else if (case_.deliveryDate === selectedDate) {
  //         // 選択日が搬入日
  //         if (timeSlot < case_.deliveryTime) {
  //             return 'transit'; // 搬入前の移動・待機
  //         } else {
  //             return 'delivery'; // 搬入時間帯
  //         }
  //     }
  //
  //     return 'transit'; // デフォルト
  // };

  // 時間帯タイプに応じたスタイルを取得 - 現在未使用
  // const getTimeSlotStyle = (type: 'pickup' | 'transit' | 'delivery') => {
  //     switch (type) {
  //         case 'pickup':
  //             return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 hover:bg-blue-200 dark:hover:bg-blue-800';
  //         case 'transit':
  //             return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 hover:bg-yellow-200 dark:hover:bg-yellow-800';
  //         case 'delivery':
  //             return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-800';
  //         default:
  //             return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 hover:bg-blue-200 dark:hover:bg-blue-800';
  //     }
  // };

  const handleGenerateAiDispatch = async () => {
    if (unassignedCases.length === 0) {
      alert('未割当の案件がありません。');
      return;
    }

    const availableTransportersForAI = availableTransporters.filter(t => t.status === 'Available');
    if (availableTransportersForAI.length === 0) {
      alert('対応可能な陸送員がいません。');
      return;
    }

    setIsGeneratingAI(true);
    setAiError('');

    try {
      // TODO: PR #109 対応 - AI配車提案の案件データ実装
      // 現在はCase[]を直接渡しているが、AI配車計算に必要な詳細データを含む
      // 拡張されたデータ構造（距離情報、時間制約、優先度等）を実装する
      const plan = await generateDispatchPlan(unassignedCases, availableTransportersForAI);
      setAiDispatchPlan(plan);
      setShowAiResults(true);
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'AI配車計画の生成に失敗しました。');
      logger.error('AI配車計画生成エラー:', err);
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const handleApplyAiAssignment = async (assignment: DispatchPlan) => {
    try {
      // AI提案の配車を適用
      for (const caseId of assignment.caseIds) {
        await updateCase(caseId, {
          assignedLandTransporterId: assignment.landTransporterId,
          status: 'assigned' as const,
        });
      }

      // 適用した配車をAI結果から削除
      setAiDispatchPlan(prev =>
        prev.filter(plan => plan.landTransporterId !== assignment.landTransporterId)
      );
    } catch (error) {
      logger.error('AI配車の適用に失敗しました:', error);
      alert('AI配車の適用に失敗しました。');
    }
  };

  const handleRejectAiAssignment = (assignment: DispatchPlan) => {
    // AI提案を拒否（削除）
    setAiDispatchPlan(prev =>
      prev.filter(plan => plan.landTransporterId !== assignment.landTransporterId)
    );
  };

  // AI配車モードの場合、自動的にAI配車を実行
  useEffect(() => {
    if (isAiMode && unassignedCases.length > 0 && !isGeneratingAI && !showAiResults) {
      handleGenerateAiDispatch();
    }
  }, [isAiMode, unassignedCases.length, isGeneratingAI, showAiResults]);

  // 陸送員の場合の表示制御ログ
  if (isDriver) {
    logger.info('🎯 Driver Schedule View Logic:', {
      isDriver,
      scheduleVisibility,
      isAdminOnly: scheduleVisibility === DriverScheduleVisibility.ADMIN_ONLY,
      isAllDrivers: scheduleVisibility === DriverScheduleVisibility.ALL_DRIVERS,
      isUndefined: scheduleVisibility === undefined,
    });
  }

  // 陸送員の設定が未読み込みの場合はローディング表示
  if (isDriver && scheduleVisibility === undefined) {
    return (
      <div className='max-w-2xl mx-auto'>
        <div className='flex justify-center items-center min-h-96'>
          <div className='text-center'>
            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4'></div>
            <p className='text-gray-600 dark:text-gray-300'>スケジュール設定を読み込み中...</p>
          </div>
        </div>
      </div>
    );
  }

  // 陸送員用の縦表示レイアウト（全ドライバービュー）
  if (isDriver) {
    return (
      <div className='max-w-2xl mx-auto'>
        <div className='flex justify-between items-center mb-6'>
          <PageTitle icon={CalendarDaysIcon} title='スケジュール' iconSize='sm' />
          <button
            onClick={() => refreshLandTransporters()}
            className='inline-flex items-center px-3 py-2 bg-blue-100 dark:bg-blue-700 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-600 transition'
            title='陸送員データを更新（勤務時間等の反映）'
          >
            <ArrowPathIcon className='h-5 w-5' />
          </button>
        </div>

        {/* 日付ナビゲーション（モバイル最適化） */}
        <div className='mb-6'>
          <div className='flex items-center justify-center space-x-2 sm:space-x-4'>
            <button
              onClick={handlePreviousDay}
              className='inline-flex items-center justify-center min-h-[44px] min-w-[44px] px-2 sm:px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition touch-target'
            >
              <ChevronLeftIcon className='h-5 w-5' />
            </button>
            <div className='text-center flex-1 sm:flex-none'>
              <div className='text-lg sm:text-xl font-bold text-gray-800 dark:text-white'>
                {formatDateForDisplay(selectedDate)}
              </div>
            </div>
            <div className='relative'>
              <input
                type='date'
                value={selectedDate}
                onChange={e => setSelectedDate(e.target.value)}
                className='absolute opacity-0 w-full h-full cursor-pointer min-h-[44px] min-w-[44px]'
                aria-label='日付選択'
              />
              <button className='inline-flex items-center justify-center min-h-[44px] min-w-[44px] px-2 sm:px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition touch-target'>
                <CalendarIcon className='h-5 w-5' />
              </button>
            </div>
            <button
              onClick={handleNextDay}
              className='inline-flex items-center justify-center min-h-[44px] min-w-[44px] px-2 sm:px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition touch-target'
            >
              <ChevronRightIcon className='h-5 w-5' />
            </button>
          </div>
        </div>

        {/* 陸送員用縦表示タイムテーブル（モバイル最適化） */}
        <div className='bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden'>
          <div className='px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600'>
            <h2 className='text-base sm:text-lg font-semibold text-gray-800 dark:text-white'>
              {user?.name || '陸送員'}の予定
            </h2>
          </div>
          <div className='relative'>
            {(() => {
              // 案件の時間範囲を計算して統合表示用のデータを作成
              const caseBlocks: Array<{
                case: Case;
                startSlotIndex: number;
                endSlotIndex: number;
                height: number;
                column: number;
                totalColumns: number;
              }> = [];

              const processedCases = new Set<string>();

              // 時間重複を検出してカラム配置を計算
              todaysCases.forEach(case_ => {
                if (processedCases.has(case_.id)) return;

                const caseStart = case_.pickupTimeStart.substring(0, 5); // "08:00:00" → "08:00"
                const caseEnd = case_.deliveryTime.substring(0, 5); // "11:30:00" → "11:30"

                const startIndex = timeSlots.findIndex(slot => slot >= caseStart);
                const endIndex = timeSlots.findIndex(slot => slot >= caseEnd);

                if (startIndex !== -1) {
                  const actualEndIndex = endIndex === -1 ? timeSlots.length : endIndex;
                  const height = (actualEndIndex - startIndex) * 80; // 80px per slot

                  // この案件と重複する既存の案件を探す
                  const overlappingBlocks = caseBlocks.filter(
                    existing =>
                      !(
                        actualEndIndex <= existing.startSlotIndex ||
                        startIndex >= existing.endSlotIndex
                      )
                  );

                  // 使用可能なカラムを見つける
                  const usedColumns = new Set(overlappingBlocks.map(block => block.column));
                  let column = 0;
                  while (usedColumns.has(column)) {
                    column++;
                  }

                  // 最大カラム数を計算
                  const maxColumns = Math.max(
                    1,
                    Math.max(...overlappingBlocks.map(b => b.totalColumns), column + 1)
                  );

                  // 重複する既存ブロックのtotalColumnsを更新
                  overlappingBlocks.forEach(block => {
                    block.totalColumns = maxColumns;
                  });

                  caseBlocks.push({
                    case: case_,
                    startSlotIndex: startIndex,
                    endSlotIndex: actualEndIndex,
                    height,
                    column,
                    totalColumns: maxColumns,
                  });

                  processedCases.add(case_.id);
                }
              });

              return (
                <>
                  {/* 時間スロットの背景 */}
                  {timeSlots.map((timeSlot, index) => (
                    <div
                      key={timeSlot}
                      className='flex min-h-[80px] border-b border-gray-200 dark:border-gray-600 last:border-b-0'
                    >
                      <div className='w-16 sm:w-20 flex-shrink-0 bg-gray-50 dark:bg-gray-700 flex items-center justify-center border-r border-gray-200 dark:border-gray-600'>
                        <span className='text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400'>
                          {timeSlot}
                        </span>
                      </div>
                      <div className='flex-1 relative'>
                        {/* 空の時間スロット表示 */}
                        {!caseBlocks.some(
                          block => index >= block.startSlotIndex && index < block.endSlotIndex
                        ) && (
                          <div className='absolute inset-0 flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm'>
                            予定なし
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* 案件ブロックの並列表示 */}
                  {caseBlocks.map((block, index) => {
                    const widthPercentage = 100 / block.totalColumns;
                    const leftPercentage = (block.column * 100) / block.totalColumns;

                    return (
                      <div
                        key={`case-${block.case.id}-${index}`}
                        className='absolute'
                        data-case-id={block.case.id}
                        style={{
                          top: `${block.startSlotIndex * 80}px`,
                          height: `${block.height}px`,
                          left: `calc(4rem + ${leftPercentage}%)`,
                          width: `calc(${widthPercentage}% - 1px)`,
                        }}
                      >
                        {/* デスクトップ用の追加スタイル */}
                        <style>{`
                                                    @media (min-width: 640px) {
                                                        [data-case-id="${block.case.id}"] {
                                                            left: calc(5rem + ${leftPercentage}%) !important;
                                                        }
                                                    }
                                                `}</style>
                        <div className='h-full mx-1 my-2'>
                          <div
                            className='h-full bg-blue-100 dark:bg-blue-900 border border-blue-300 dark:border-blue-700 rounded-lg p-2 cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-800 transition touch-target flex flex-col justify-center'
                            onClick={() => navigateToCaseDetail(block.case.id)}
                          >
                            <div className='min-w-0'>
                              <div className='text-xs font-medium text-gray-900 dark:text-white truncate'>
                                {getVehicleNameForSchedule(block.case)}
                              </div>
                              <TimeDisplay
                                pickupTimeStart={block.case.pickupTimeStart}
                                pickupTimeEnd={block.case.pickupTimeEnd}
                                deliveryTime={block.case.deliveryTime}
                                textSize='text-xs'
                                spacing='space-y-1'
                              />
                              <div className='text-xs text-gray-500 dark:text-gray-400 mt-1 space-y-1'>
                                <div className='truncate'>{getPickupPartnerName(block.case)}</div>
                                <div className='truncate'>{getDeliveryPartnerName(block.case)}</div>
                              </div>
                              <div
                                className={`inline-flex items-center px-1 py-0.5 rounded text-xs font-medium ${getCaseStatusClass(block.case.status)} mt-1`}
                              >
                                {getCaseStatusText(block.case.status)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>
              );
            })()}
          </div>
        </div>
      </div>
    );
  }

  // 管理者・営業担当者用の横表示レイアウト（既存のコード）
  return (
    <ScheduleDragDropContext
      onDragStart={handleDndDragStart}
      onDragOver={handleDndDragOver}
      onDragEnd={handleDndDragEnd}
      activeCase={dragDropResult.activeCase || activeCase}
      canDrop={dragDropResult.canDrop}
      dropReason={dragDropResult.dropReason}
    >
      <div className='max-w-7xl mx-auto'>
        <div className='flex justify-between items-center mb-6'>
          {isAiMode ? (
            <PageTitle
              icon={SparklesIcon}
              title='AI自動配車'
              iconSize='lg'
              className='text-purple-600'
            />
          ) : (
            <PageTitle
              icon={CalendarDaysIcon}
              title={
                isDriver && scheduleVisibility === DriverScheduleVisibility.ALL_DRIVERS
                  ? 'チームスケジュール'
                  : '陸送員スケジュール'
              }
              iconSize='lg'
            />
          )}
          <div className='flex items-center space-x-3'>
            {/* 時間軸表示モード切り替え */}
            <div className='flex items-center space-x-2'>
              <label
                htmlFor='timeSlotMode'
                className='text-sm font-medium text-gray-700 dark:text-gray-300'
              >
                時間軸:
              </label>
              <select
                id='timeSlotMode'
                value={timeSlotMode}
                onChange={e => setTimeSlotMode(e.target.value as 'dynamic' | 'fixed')}
                className='px-3 py-1 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
              >
                <option value='dynamic'>動的表示</option>
                <option value='fixed'>固定表示</option>
              </select>
            </div>

            {/* FL-325: 表示倍率コントロール */}
            <div className='flex items-center space-x-2'>
              <label className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                表示倍率:
              </label>
              <div className='flex space-x-1'>
                {([100, 90, 80] as const).map(scale => (
                  <button
                    key={scale}
                    role='button'
                    aria-pressed={scheduleScale === scale}
                    onClick={() => setScheduleScale(scale)}
                    className={`px-3 py-1 text-sm rounded-md transition ${
                      scheduleScale === scale
                        ? 'bg-primary-600 text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    {scale}%
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={() => refreshLandTransporters()}
              className='inline-flex items-center px-3 py-2 bg-blue-100 dark:bg-blue-700 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-600 transition'
              title='陸送員データを更新（勤務時間等の反映）'
            >
              <ArrowPathIcon className='h-5 w-5 mr-1' />
              <span className='text-sm'>陸送員更新</span>
            </button>
          </div>
        </div>

        {/* 日付ナビゲーション */}
        <div className='mb-8'>
          <div className='flex items-center justify-center space-x-4'>
            <button
              onClick={handlePreviousDay}
              className='inline-flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition'
            >
              <ChevronLeftIcon className='h-5 w-5' />
            </button>

            <div className='flex items-center space-x-3'>
              <div className='text-center'>
                <div className='text-2xl font-bold text-gray-800 dark:text-white'>
                  {formatDateForDisplay(selectedDate)}
                </div>
              </div>
            </div>

            <div className='relative'>
              <input
                type='date'
                value={selectedDate}
                onChange={e => setSelectedDate(e.target.value)}
                className='absolute opacity-0 w-full h-full cursor-pointer'
              />
              <button className='inline-flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition'>
                <CalendarIcon className='h-5 w-5' />
              </button>
            </div>

            <button
              onClick={handleNextDay}
              className='inline-flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition'
            >
              <ChevronRightIcon className='h-5 w-5' />
            </button>
          </div>
        </div>

        {/* AI配車モードの場合の特別な表示 */}
        {isAiMode && (
          <div className='mb-6 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4'>
            <div className='flex items-center justify-between mb-3'>
              <div className='flex items-center'>
                <SparklesIcon className='h-5 w-5 text-purple-500 mr-2' />
                <h3 className='text-lg font-medium text-purple-800 dark:text-purple-200'>
                  AI自動配車モード
                </h3>
              </div>
              <button
                onClick={() => setSearchParams({})}
                className='text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-200 text-sm'
              >
                通常モードに戻る
              </button>
            </div>
            <p className='text-sm text-purple-700 dark:text-purple-300'>
              未割り当て案件に対してAIが最適な配車プランを提案します。提案を確認して適用してください。
            </p>
          </div>
        )}

        {/* 未割り当て案件リスト（陸送員には非表示） */}
        {unassignedCases.length > 0 && !isDriver && (
          <DroppableZone
            id='unassigned-zone'
            isunassigned={true}
            className={`mb-6 ${isAiMode ? 'bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'} rounded-lg p-4`}
          >
            <div>
              <div className='flex items-center justify-between mb-3'>
                <div className='flex items-center'>
                  {isAiMode ? (
                    <SparklesIcon className='h-5 w-5 text-purple-500 mr-2' />
                  ) : (
                    <ExclamationTriangleIcon className='h-5 w-5 text-red-500 mr-2' />
                  )}
                  <h3
                    className={`text-sm font-medium ${isAiMode ? 'text-purple-800 dark:text-purple-200' : 'text-red-800 dark:text-red-200'}`}
                  >
                    未割り当て案件 ({unassignedCases.length}件)
                  </h3>
                </div>

                {/* AI配車ボタン */}
                {!isAiMode && (
                  <button
                    onClick={handleGenerateAiDispatch}
                    disabled={isGeneratingAI || unassignedCases.length === 0}
                    className='inline-flex items-center px-3 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white text-xs font-medium rounded-md transition-colors'
                  >
                    <SparklesIcon className='h-4 w-4 mr-1' />
                    {isGeneratingAI ? 'AI配車中...' : 'AI配車'}
                  </button>
                )}
              </div>
              <div className='grid grid-cols-1 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto p-2'>
                {unassignedCases.map(case_ => {
                  // 未割り当て案件用のduration計算（引き取り開始から終了まで）
                  const startTime = case_.pickupTimeStart;
                  const endTime = case_.pickupTimeEnd;
                  const startHour = parseInt(startTime.split(':')[0]);
                  const startMinute = parseInt(startTime.split(':')[1]);
                  const endHour = parseInt(endTime.split(':')[0]);
                  const endMinute = parseInt(endTime.split(':')[1]);
                  const duration = endHour * 60 + endMinute - (startHour * 60 + startMinute);
                  const durationInSlots = Math.max(1, Math.round(duration / 30)); // 30分スロット換算

                  return (
                    <div key={case_.id} className='relative'>
                      <DraggableCase
                        case={case_}
                        onUnassign={handleUnassignCase}
                        timeSlotHeight={20}
                        duration={durationInSlots}
                        getVehicleName={getVehicleNameForSchedule}
                        getBillingPartnerName={getBillingPartnerName}
                        getPickupPartnerName={getPickupPartnerName}
                        getDeliveryPartnerName={getDeliveryPartnerName}
                      />
                    </div>
                  );
                })}
              </div>
              <p
                className={`text-xs ${isAiMode ? 'text-purple-700 dark:text-purple-300' : 'text-red-700 dark:text-red-300'} mt-2`}
              >
                {isAiMode
                  ? 'AI配車システムが最適な陸送員選択理由と一緒に提案します。下部のAI提案を確認してください'
                  : '案件をドラッグして下記の陸送員タイムテーブルにドロップすることで割り当てできます'}
              </p>
            </div>
          </DroppableZone>
        )}

        {/* AI配車結果表示 */}
        {(showAiResults ||
          (isAiMode && (isGeneratingAI || aiDispatchPlan.length > 0 || aiError))) && (
          <div className='mb-6 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg'>
            <div className='flex items-center justify-between p-4 pb-0'>
              <div className='flex items-center'>
                <SparklesIcon className='h-5 w-5 text-purple-500 mr-2' />
                <h3 className='text-sm font-medium text-purple-800 dark:text-purple-200'>
                  AI配車提案 ({aiDispatchPlan.length}件)
                </h3>
              </div>
              <button
                onClick={handleCloseAiResults}
                className='inline-flex items-center px-2 py-1 text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-200'
              >
                <XMarkIcon className='h-4 w-4' />
              </button>
            </div>

            {aiError ? (
              <div className='p-4'>
                <div className='bg-red-100 dark:bg-red-900/40 border border-red-300 dark:border-red-700 rounded-md p-3'>
                  <p className='text-sm text-red-800 dark:text-red-200'>{aiError}</p>
                  <p className='text-xs text-red-600 dark:text-red-400 mt-1'>
                    手動配車で継続してください。
                  </p>
                </div>
              </div>
            ) : (
              <AiReasonDisplay
                dispatchPlans={aiDispatchPlan}
                onApply={handleApplyAiAssignment}
                onReject={handleRejectAiAssignment}
              />
            )}
          </div>
        )}

        {/* 陸送員別タイムテーブル（AI配車モードでは表示しない）- FL-158: 最適化レイアウト */}
        {!isAiMode &&
          (availableTransporters.length === 0 ? (
            <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center'>
              <CalendarDaysIcon className='mx-auto h-12 w-12 text-gray-400 mb-4' />
              <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-2'>
                勤務予定の陸送員がいません
              </h3>
              <p className='text-gray-500 dark:text-gray-400'>
                この日に勤務予定の陸送員がいません。
              </p>
            </div>
          ) : (
            <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden'>
              {/* FL-325: スケールコンテナ - ズーム機能適用 */}
              <div
                data-testid='schedule-scale-container'
                style={{
                  transform: `scale(${scheduleScale / 100})`,
                  transformOrigin: 'top left',
                  width: `${(100 / scheduleScale) * 100}%`,
                }}
              >
                {/* 横スクロールコンテナ */}
                <div className='overflow-x-auto' style={{ maxHeight: 'calc(100vh - 350px)' }}>
                  <div style={{ minWidth: `${192 + timeSlots.length * 80}px` }}>
                    {/* 共通時間軸ヘッダー（sticky） */}
                    <SharedTimeAxisHeader timeSlots={timeSlots} />

                    {/* 陸送員行リスト */}
                    {/* FL-563: availableTransporters は既に driver_schedules でフィルタリング済み */}
                    <div>
                      {availableTransporters.map(transporter => {
                        return (
                          <TransporterRowOptimized
                            key={transporter.id}
                            transporter={transporter}
                            timeSlots={timeSlots}
                            cases={todaysCases}
                            selectedDayOfWeek={selectedDayOfWeek}
                            selectedDate={selectedDate}
                            dragOverTransporter={dragOverTransporter}
                            canDrop={canDrop}
                            dropReason={dropReason}
                            activeCase={activeCase}
                            onUnassignCase={handleUnassignCase}
                            onTransporterClick={setSelectedTransporter}
                            onTimeSlotClick={handleTimeSlotClick}
                            getVehicleName={getVehicleNameForSchedule}
                            getBillingPartnerName={getBillingPartnerName}
                            getPickupPartnerName={getPickupPartnerName}
                            getDeliveryPartnerName={getDeliveryPartnerName}
                            registeredSchedule={scheduleCache[transporter.id]}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

        {/* スケジュール表示説明（AI配車モードと陸送員には非表示） */}
        {!isAiMode && !isDriver && (
          <div className='mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4'>
            <h3 className='text-sm font-medium text-blue-800 dark:text-blue-200 mb-2'>
              スケジュール表示について
            </h3>
            <ul className='text-sm text-blue-700 dark:text-blue-300 space-y-1'>
              <li>• 未割り当ての案件を陸送員の時間帯にドラッグ&ドロップして割り当てできます</li>
              <li>• 案件は引き取り開始から搬入完了まで連続したボタンとして表示されます</li>
              <li>
                •{' '}
                <span className='inline-block w-4 h-3 bg-gradient-to-r from-blue-200 via-yellow-200 to-green-200 rounded mr-1'></span>
                グラデーションバー: 同日引き取り→搬入
              </li>
              <li>
                •{' '}
                <span className='inline-block w-4 h-3 bg-gradient-to-r from-blue-200 to-yellow-200 rounded mr-1'></span>
                青→黄バー: 引き取り→翌日以降搬入
              </li>
              <li>
                • <span className='inline-block w-3 h-3 bg-yellow-200 rounded mr-1'></span>黄色バー:
                前日引き取り済み、移動・待機中
              </li>
              <li>• 灰色の時間帯は勤務時間外のため割り当てできません</li>
              <li>• 引き取りから搬入完了まで全体の時間で競合チェックが行われます</li>
              <li>• 「動的表示」モードでは、陸送員の稼働時間に合わせて時間軸が自動調整されます</li>
            </ul>
          </div>
        )}

        {/* フローティング未割り当てゾーン（ドラッグ中のみ表示） */}
        {activeCase && (
          <DroppableZone
            id='floating-unassigned-zone'
            isunassigned={true}
            className='fixed bottom-4 right-4 w-48 h-24 bg-red-100 dark:bg-red-900/20 border-2 border-red-500 dark:border-red-600 border-dashed rounded-lg flex items-center justify-center shadow-lg z-50 transition-all duration-200 hover:bg-red-200 dark:hover:bg-red-900/30'
          >
            <div className='text-center'>
              <div className='text-red-700 dark:text-red-300 font-medium text-sm'>
                ここにドロップで
              </div>
              <div className='text-red-600 dark:text-red-400 text-xs'>未割り当てに戻す</div>
            </div>
          </DroppableZone>
        )}

        {/* 陸送員詳細モーダル */}
        <LandTransporterDetailModal
          transporter={selectedTransporter}
          onClose={() => setSelectedTransporter(null)}
          vehicleTypes={vehicleTypes}
          canViewBusinessConstraints={canViewBusinessConstraints}
          onEdit={transporter => {
            // TODO: 陸送員編集機能実装予定
            logger.info('陸送員編集:', transporter);
            setSelectedTransporter(null);
          }}
          onDelete={transporter => {
            // TODO: 陸送員削除機能実装予定
            logger.info('陸送員削除:', transporter);
            setSelectedTransporter(null);
          }}
        />

        {/* FL-163: スケジュール直接登録モーダル */}
        {isDirectRegistrationModalOpen && selectedTimeSlot && (
          <ScheduleDirectRegistrationModal
            isOpen={isDirectRegistrationModalOpen}
            transporterId={selectedTimeSlot.transporterId}
            date={selectedTimeSlot.date}
            timeStart={selectedTimeSlot.timeSlot}
            transporters={landTransporters}
            onSave={handleDirectRegistrationSave}
            onCancel={handleDirectRegistrationCancel}
          />
        )}
      </div>
    </ScheduleDragDropContext>
  );
};

export default SchedulePage;
